<template>
    <div>
        <label>{{ i18n("voucherDesign.variables") }}:</label>
        <div>
            <button
                v-for="variable in variables"
                type="button"
                :key="variable.name"
                @mousedown="onMouseDown"
                @click="insertVariable(variable)"
                class="btn btn-sm btn-outline-success m-1"
                :title="brToNewLine(variable.label)"
            >
                {{ variable.title }}
                <strong v-if="variable.required" class="text-danger">*</strong>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ILocationDetail } from "@/data/model/Location";
    import { i18n } from "@/portal/Trans";
    import { addAbsoluteTextBoxWithContent } from "@/portal/components/vouchers/editor/extensions/AbsoluteTextBox";
    import {
        insertContentAtCursor,
        isInsideAbsoluteTextBox,
    } from "@/portal/components/vouchers/editor/helpers/editorUtils";
    import type { Editor } from "@tiptap/vue-3";
    import { type Ref, computed, inject, ref } from "vue";

    const isFocusRecentlyLost = inject<Ref<boolean>>("isFocusRecentlyLost");
    const wasFocusRecentlyLostOnMouseDown = ref<boolean>(false);

    const props = defineProps<{
        editor: Editor;
        locationDetail: ILocationDetail | undefined;
    }>();

    const locationName = computed(() => props.locationDetail?.name);
    const locationAddress = computed(() => {
        const address = props.locationDetail?.address;
        if (address) {
            let addressString = `${address.street}<br>${address.zip}, ${address.city}`;
            if (address.country) {
                addressString += `<br>${address.country}`;
            }
            return addressString;
        }
        return "- no address -";
    });
    const locationInvoiceAddress = computed(() => {
        const billingAddress = props.locationDetail?.billingAddress;
        if (billingAddress) {
            let billingAddressString =
                `${props.locationDetail.company}<br>` +
                `${billingAddress.street}<br>` +
                `${billingAddress.zip}, ${billingAddress.city}`;

            if (billingAddress.country) {
                billingAddressString += `<br>${i18n(`enums.Country.${billingAddress.country}`)}`;
            }
            return billingAddressString;
        }
        return "- no address -";
    });

    const variables = [
        {
            name: "VoucherBalance",
            label: "CHF 300.00",
            title: i18n("voucherDesign.voucherBalance"),
            required: true,
        },
        {
            name: "VoucherCode",
            label: "VNF34I43N3OF43OINF3OAVS34VNF34I",
            title: i18n("voucherDesign.voucherCode"),
            required: true,
        },
        {
            name: "ExpirationDate",
            label: (() => {
                const nextYear = new Date().getFullYear() + 1;
                return `31.12.${nextYear} 23:59`;
            })(),
            title: i18n("voucherDesign.expirationDate"),
            required: true,
        },
        { name: "LocationName", label: locationName.value, title: i18n("voucherDesign.locationName") },
        {
            name: "LocationAddress",
            label: locationAddress.value,
            title: i18n("voucherDesign.locationAddress"),
        },
        {
            name: "InvoiceAddress",
            label: locationInvoiceAddress.value,
            title: i18n("voucherDesign.invoiceAddress"),
        },
    ];

    function insertVariable(variable: { name: string; label: string | undefined; title: string }) {
        console.log("insertVariable - wasFocusRecentlyLostOnMouseDown", wasFocusRecentlyLostOnMouseDown.value);
        const variableContent = {
            type: "documentVariable",
            attrs: {
                name: variable.name,
                label: variable.label ?? "",
                title: variable.title,
            },
        };

        if (isInsideAbsoluteTextBox(props.editor) && wasFocusRecentlyLostOnMouseDown?.value) {
            console.log("insertVariable to absolute text box", variable);
            insertContentAtCursor(props.editor, variableContent);
        } else {
            console.log("insertVariable to document", variable);
            addAbsoluteTextBoxWithContent(props.editor, [
                {
                    type: "paragraph",
                    content: [variableContent],
                },
            ]);
        }
        wasFocusRecentlyLostOnMouseDown.value = false;
    }

    function getRequiredVariables() {
        return variables.filter((variable) => variable.required).map((variable) => variable.name);
    }

    function brToNewLine(text: string | undefined) {
        if (text) {
            return text.replace(/<br\s*\/?>/gi, " ");
        }
        return "";
    }

    function onMouseDown() {
        window.setTimeout(() => {
            wasFocusRecentlyLostOnMouseDown.value = isFocusRecentlyLost?.value ?? false;
        }, 10);
    }

    defineExpose({
        getRequiredVariables,
    });
</script>
